import { compact, isEmpty, map, orderBy, reduce, uniq, values } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import ExternalLink from "../../../../components/ExternalLink";
import ExternalLinkIcon from "../../../../components/icons/ExternalLinkIcon";
import InfoIcon from "../../../../components/icons/InfoIcon";
import NavLink from "../../../../components/NavLink";
import { useAuthentication } from "../../../../context/authentication";
import { useGoogleAnalytics } from "../../../../context/googleAnalytics";
import { useHotspots } from "../../../../context/hotspots";
import { useLocations } from "../../../../context/locations";
import { useTransmitScales } from "../../../../context/transmitScales";
import { getHexDensityLimit } from "../../../../context/transmitScales/TransmitScalesProvider";
import classNames from "../../../../utils/classNames";
import {
  ClassificationValue,
  getClassification
} from "../../../../utils/hotspotUtils";
import {
  getLocationsInHex,
  h3ToParentMemoized
} from "../../../../utils/memoizedMethods";
import { Hotspot, HotspotLocation } from "../../../../utils/types";
import HexDashboardTab from "./HexDashboardTab";
import HotspotsInHexTab from "./HotspotsInHexTab";
import LocationsInHexTab from "./LocationsInHexTab";
import NeighborHexesTab, { getHexNeighborsWithLabel } from "./NeighborHexesTab";

const HexTransmitScaleTab: React.FC<
  RouteComponentProps<{ h3Index: string }>
> = (props) => {
  const { wrapActionWithAuth } = useAuthentication();
  const { locations, simulatedLocations, setSimulatedLocations } =
    useLocations();
  const { trackEvent } = useGoogleAnalytics();
  const { hotspotsMap, hotspotsAroundCenter } = useHotspots();
  const {
    isInteractiveHotspotEnhanced,
    getHexTransmitScaleInfo,
    generateLocationsToReachTargetDensity,
    isTransmitScaleActive,
    simulatedDisabledIds,
    setSimulatedDisabledIds,
    calculatedTransmitScales
  } = useTransmitScales();

  const {
    resolution,
    transmitScale,
    limit,
    hip17ChainVars: { densityTarget, densityMax, numberOfSiblings },
    densityStatusMessage,
    excess,
    limitCanBeIncreased,
    unclippedDensity,
    clippedDensity,
    occupiedCount,
    shortOfDensityTarget
  } = getHexTransmitScaleInfo(props.match.params.h3Index);

  const detailsPageMatch = matchPath<{
    h3Index: string;
    subsection: string;
  }>(props.location.pathname, {
    path: "/hexes/:h3Index/transmit-scale/:subsection"
  });

  const locationsInHex = useMemo(
    () => getLocationsInHex(values(locations), props.match.params.h3Index),
    [locations, props.match.params.h3Index]
  );

  const hotspotsInHex = useMemo(
    () =>
      hotspotsAroundCenter.filter(
        ({ location }) =>
          h3ToParentMemoized(location, resolution) ===
          props.match.params.h3Index
      ),
    [hotspotsAroundCenter, props.match.params.h3Index, resolution]
  );

  const renderOptimizationContent = useMemo(() => {
    let strategiesDescription;
    const cards: {
      key: string;
      title: string;
      text: string;
      action?: () => void;
      actionButtonText: string;
      hide?: boolean;
    }[] = [];

    if (excess <= 0) {
      strategiesDescription = "This hex has an optimal transmit scale of 1 🥳";
    } else {
      strategiesDescription = "Rewards are affected by this hex. ";

      const eliminationStrategyCard = {
        key: "strategy-1",
        title: "Strategy 1",
        text: `Eliminate ${excess} hotspot${
          excess > 1 ? "s" : ""
        } or planned location${excess > 1 ? "s" : ""} from this hex.`,
        action: async () => {
          trackEvent("simulate", {
            event_category: "engagement",
            event_label: "strategy 1"
          });

          let toEliminate = excess;
          let disabledLocations: HotspotLocation[] = [];
          let disabledHotspots: Hotspot[] = [];

          locationsInHex
            .filter((location) => isTransmitScaleActive(location.uuid))
            .forEach((location) => {
              toEliminate--;
              disabledLocations.push(location);
            });

          if (disabledLocations.length > 0) {
            props.history.push(`${props.match.url}/locations-in-hex`);
          } else {
            props.history.push(`${props.match.url}/hotspots-in-hex`);
          }

          orderBy(
            hotspotsInHex.filter(({ id, last_poc_challenge }) =>
              isInteractiveHotspotEnhanced(id, last_poc_challenge)
            ),
            (hotspot) => calculatedTransmitScales[hotspot.id] || 0,
            // This has been changed from worse performing to descending transmit scales.
            // That's worse than before but it is more likely to resolve the issue in just one iteration, due to the clipped densities logic.
            "desc"
          ).forEach((hotspot) => {
            if (toEliminate > 0) {
              toEliminate--;
              disabledHotspots.push(hotspot);
            }
          });

          setSimulatedDisabledIds((prevSimulatedDisabledIds) =>
            uniq([
              ...prevSimulatedDisabledIds,
              ...map(disabledLocations, "uuid"),
              ...map(disabledHotspots, "id")
            ])
          );
        },
        actionButtonText: "Simulate"
      };

      cards.push(eliminationStrategyCard);
    }

    if (excess > 0 && limitCanBeIncreased) {
      let requiredNeighborsForIncrease = 0;
      let calculatedDensityLimit = limit;

      while (
        unclippedDensity > calculatedDensityLimit &&
        occupiedCount + requiredNeighborsForIncrease < 7 &&
        calculatedDensityLimit < densityMax
      ) {
        requiredNeighborsForIncrease++;
        calculatedDensityLimit = getHexDensityLimit(
          numberOfSiblings,
          densityTarget,
          densityMax,
          occupiedCount + requiredNeighborsForIncrease
        );
      }

      const strategyCard = {
        key: "strategy-2",
        title: "Strategy 2",
        text: `Increase the limit by reaching the density target for at least ${requiredNeighborsForIncrease} highlighted neighbor hex${
          requiredNeighborsForIncrease > 1 ? "es" : ""
        } below.`,
        action: () => {
          trackEvent("simulate", {
            event_category: "engagement",
            event_label: "strategy 2"
          });

          props.history.push(`${props.match.url}/neighbor-hexes`);

          const neighborsWithLabels = getHexNeighborsWithLabel(
            props.match.params.h3Index
          );

          let neighborsToReachTarget = requiredNeighborsForIncrease;
          let locationsToAdd: HotspotLocation[] = [];
          let fulfilledNeighbors: string[] = [];

          orderBy(
            neighborsWithLabels
              .map(([h3Index, label]) => {
                const { shortOfDensityTarget, resolution } =
                  getHexTransmitScaleInfo(h3Index);
                return {
                  h3Index,
                  label,
                  shortOfDensityTarget,
                  resolution
                };
              })
              .filter(({ shortOfDensityTarget }) => shortOfDensityTarget > 0),
            "shortOfDensityTarget",
            "asc"
          ).forEach(({ h3Index, label }) => {
            if (neighborsToReachTarget === 0) {
              return;
            }

            const locationsToFillNeighbor =
              generateLocationsToReachTargetDensity(
                h3Index,
                (locationNumber) =>
                  `Strategy 2, location ${
                    locationsToAdd.length + locationNumber
                  }`
              );

            locationsToAdd = locationsToAdd.concat(locationsToFillNeighbor);
            fulfilledNeighbors.push(label);
            neighborsToReachTarget--;
          });

          setSimulatedLocations((prevLocations) =>
            prevLocations.concat(locationsToAdd)
          );
        },
        actionButtonText: "Simulate"
      };

      if (clippedDensity > densityMax) {
        strategyCard.text += ` Even at the maximum limit of ${densityMax}, there will still be an excess of ${
          clippedDensity - densityMax
        } so a combination of both strategies is advised.`;
      }

      cards.push(strategyCard);
      strategiesDescription += "Two strategies can improve the situation:";
    } else if (excess > 0) {
      strategiesDescription += `As the limit of this hex has been increased to the maximum of ${densityMax}, there is only one strategy that can improve the situation:`;
    }

    const strategy1DisabledHotspotIds = compact(
      simulatedDisabledIds.filter((id) => hotspotsMap[id])
    );
    const strategy1DisabledLocationUuids = compact(
      simulatedDisabledIds.filter((uuid) => locations[uuid])
    );
    if (
      !isEmpty(strategy1DisabledLocationUuids) ||
      !isEmpty(strategy1DisabledHotspotIds)
    ) {
      let message = "";

      if (strategy1DisabledLocationUuids.length > 0) {
        message += `${strategy1DisabledLocationUuids.length} planned location${
          strategy1DisabledLocationUuids.length > 1 ? "s were" : " was"
        } disabled. `;
      }

      if (strategy1DisabledHotspotIds.length > 0) {
        message += `${strategy1DisabledHotspotIds.length} hotspot${
          strategy1DisabledHotspotIds.length > 1 ? "s were" : " was"
        } disabled. `;
      }

      if (excess > 0) {
        message +=
          "This improved the transmit scale for this hex. Run the above simulation again to improve it further.";
      } else {
        message += "This solved the transmit scale problems for this hex.";
      }

      const strategy1ResultsCard = {
        key: "strategy-1-results",
        title: "Strategy 1 results",
        text: message,
        action: () => {
          setSimulatedDisabledIds([]);
        },
        actionButtonText: "Undo strategy"
      };

      cards.push(strategy1ResultsCard);
    }

    if (!isEmpty(simulatedLocations)) {
      const fulfilledNeighbors = reduce(
        getHexNeighborsWithLabel(props.match.params.h3Index),
        (acc: string[], [neighborH3Index, letter]) => {
          const addedLocationsInNeighborHex = getLocationsInHex(
            simulatedLocations,
            neighborH3Index
          );

          if (!isEmpty(addedLocationsInNeighborHex)) acc.push(letter);

          return acc;
        },
        []
      );

      const strategy2ResultsCard = {
        key: "strategy-2-results",
        title: "Strategy 2 results",
        text: `The density limit was increased by reaching the target density for neighbor hex${
          fulfilledNeighbors.length > 1 ? "es" : ""
        } ${fulfilledNeighbors.join(", ")} by adding ${
          simulatedLocations.length
        } location${
          simulatedLocations.length > 1 ? "s" : ""
        } in total. This was the least amount of added locations needed to improve the situation with strategy 2.`,
        action: () => {
          setSimulatedLocations([]);
        },
        actionButtonText: "Undo strategy"
      };

      cards.push(strategy2ResultsCard);
    }

    return (
      <>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
          {strategiesDescription}
        </p>

        {!isEmpty(cards) && (
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {cards
              .filter(({ hide }) => !hide)
              .map(({ key, title, text, action, actionButtonText }) => (
                <div
                  className="bg-gray-50 dark:bg-gray-800 sm:rounded-lg"
                  key={key}
                >
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                          {title}
                        </h3>
                      </div>

                      <div className="flex-shrink-0 flex items-center">
                        <button
                          onClick={action && wrapActionWithAuth(action)}
                          type="button"
                          className="px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 disabled:opacity-50 disabled:cursor-default"
                        >
                          {actionButtonText}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 max-w-xl text-sm text-gray-500 dark:text-gray-300">
                      <p>{text}</p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </>
    );
  }, [
    calculatedTransmitScales,
    clippedDensity,
    densityMax,
    densityTarget,
    excess,
    generateLocationsToReachTargetDensity,
    getHexTransmitScaleInfo,
    hotspotsInHex,
    hotspotsMap,
    isInteractiveHotspotEnhanced,
    isTransmitScaleActive,
    limit,
    limitCanBeIncreased,
    locations,
    locationsInHex,
    numberOfSiblings,
    occupiedCount,
    props.history,
    props.match.params.h3Index,
    props.match.url,
    setSimulatedDisabledIds,
    setSimulatedLocations,
    simulatedDisabledIds,
    simulatedLocations,
    trackEvent,
    unclippedDensity,
    wrapActionWithAuth
  ]);

  const tabs = useMemo(
    () => [
      {
        key: "neighbor-hexes",
        title: "Neighbor hexes",
        count: 6
      },
      {
        key: "hotspots-in-hex",
        title: "Hotspots in hex",
        count: hotspotsInHex.length
      },
      {
        key: "locations-in-hex",
        title: "Locations in hex",
        count: locationsInHex.length
      },
      {
        key: "dashboard",
        title: "Dashboard"
      }
    ],
    [hotspotsInHex.length, locationsInHex.length]
  );

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-200 truncate">
              <span className="capitalize mr-1">
                {getClassification(transmitScale)}
              </span>
              Transmit Scale
              <Tooltip
                placement="top"
                overlay={
                  <>
                    <ExternalLink
                      className="inline-flex text-xs mr-1"
                      text="HIP17"
                      url="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
                    />
                    rewards hotspots transmitting from a given location based on
                    how many other hotspots can transmit from the same area. It
                    assigns a transmit scale which gets lower the more saturated
                    the area is so that a redundancy is met but oversaturation
                    is disincentivized. PoC rewards are multiplied by the
                    transmit scale of the transmitting hotspot.
                  </>
                }
              >
                <InfoIcon className="inline-flex h-4 w-4 ml-1" />
              </Tooltip>
            </dt>

            <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
              <ClassificationValue
                value={transmitScale}
                hexProps={{
                  width: 20,
                  height: 22.8
                }}
              />
            </dd>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-200 truncate flex items-center justify-between">
              <div className="flex items-center">
                <span>Density</span>

                {!(isEmpty(hotspotsInHex) && isEmpty(locationsInHex)) && (
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        <div className="mb-1">
                          Clipped density for calculations: {clippedDensity}
                        </div>

                        <div className="mb-1">
                          Interactive hotspots:{" "}
                          {
                            hotspotsInHex.filter(({ id, last_poc_challenge }) =>
                              isInteractiveHotspotEnhanced(
                                id,
                                last_poc_challenge
                              )
                            ).length
                          }
                        </div>

                        <div className="mb-1">
                          Non interactive hotspots:{" "}
                          {hotspotsInHex.length -
                            hotspotsInHex.filter(({ id, last_poc_challenge }) =>
                              isInteractiveHotspotEnhanced(
                                id,
                                last_poc_challenge
                              )
                            ).length}
                        </div>

                        <div className="mb-1">
                          Activated planned locations:{" "}
                          {
                            locationsInHex.filter((location) =>
                              isTransmitScaleActive(location.uuid)
                            ).length
                          }
                        </div>

                        <div className="mb-1">
                          Disabled planned locations:{" "}
                          {locationsInHex.length -
                            locationsInHex.filter((location) =>
                              isTransmitScaleActive(location.uuid)
                            ).length}
                        </div>

                        {excess !== 0 && (
                          <div>
                            Technical note: If there are problematic hexes
                            inside the current hex, this number might not
                            correspond with the actual number of
                            hotspots/locations in this hex, because calculations
                            are based on 'clipped density'. See{" "}
                            <ExternalLink
                              className="inline-flex text-xs mr-1"
                              text="HIP17"
                              url="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
                            />
                            .
                          </div>
                        )}
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                )}
              </div>
            </dt>

            <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
              {densityStatusMessage}
            </dd>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-200 truncate flex items-center justify-between">
              <div className="flex items-center">
                <span>Limit</span>
                <Tooltip
                  placement="top"
                  overlay={`This hex has a limit of ${limit} interactive hotspot${
                    limit > 1 ? "s" : ""
                  } or planned locations included in the transmit scale simulation.`}
                >
                  <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                </Tooltip>
              </div>
            </dt>

            <dd className="flex items-baseline">
              <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {limit}
              </div>

              <div className="ml-2 flex items-baseline text-sm text-gray-500 dark:text-gray-300">
                Interactive hotspot{limit > 1 ? "s" : ""}
              </div>
            </dd>
          </div>
        </div>
      </dl>

      <div className="mt-6">
        <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Hex info
          </h3>
        </div>

        <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
          Based on the{" "}
          <a
            className="inline-flex items-center hover:text-hotspotty-500 truncate"
            href="https://docs.helium.com/blockchain/blockchain-primitives/"
            target="_blank"
            rel="noreferrer"
          >
            blockchain variables
            <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
          </a>{" "}
          this resolution {resolution} hex has a{" "}
          <span className="font-bold">target density of {densityTarget} </span>
          <a
            className="inline-flex items-center hover:text-hotspotty-500 truncate"
            href="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md#getting-interactive-and-losing-interactive-status"
            target="_blank"
            rel="noreferrer"
          >
            interactive
            <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
          </a>{" "}
          hotspot
          {densityTarget > 1 ? "s" : ""} (status:{" "}
          <span className="font-bold">
            {shortOfDensityTarget <= 0
              ? "target reached"
              : `${shortOfDensityTarget} to be added`}
          </span>
          ). When this limit is exceeded, the transmit scale drops below 1. This
          limit can be increased to a maximum of {densityMax} depending on the
          number of neighbor hexes that reached the target density of{" "}
          {densityTarget}.
        </p>
      </div>

      <div className="mt-6">
        <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Optimization
          </h3>
        </div>

        {renderOptimizationContent}
      </div>

      <div className="my-6">
        <select
          className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
          onChange={(e) => {
            props.history.push(`${props.match.url}/${e.target.value}`);
          }}
          value={detailsPageMatch?.params.subsection}
        >
          {tabs.map(({ key, title, count }) => (
            <option key={key} value={key}>
              {title}
              {count ? ` (${count})` : null}
            </option>
          ))}
        </select>
        <div className="hidden sm:block">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map(({ key, title, count }) => (
              <NavLink
                key={key}
                to={`${props.match.url}/${key}`}
                activeClassName="bg-hotspotty-100 text-hotspotty-700 dark:text-white dark:bg-gray-700 dark:text-white"
                inactiveClassName="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-transparent"
                className="px-3 py-2 font-medium text-sm rounded-md flex items-center group"
              >
                {title}
                {count ? (
                  <span
                    className={classNames(
                      detailsPageMatch?.params.subsection === key
                        ? "bg-hotspotty-400 text-gray-100 dark:bg-gray-500"
                        : "bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500",
                      "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block"
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <Switch>
        <Route
          path={`${props.match.path}/neighbor-hexes`}
          component={NeighborHexesTab}
        />
        <Route
          path={`${props.match.path}/hotspots-in-hex`}
          component={HotspotsInHexTab}
        />
        <Route
          path={`${props.match.path}/locations-in-hex`}
          component={LocationsInHexTab}
        />
        <Route
          path={`${props.match.path}/dashboard`}
          component={HexDashboardTab}
        />
        <Redirect to={`${props.match.path}/neighbor-hexes`} />
      </Switch>
    </div>
  );
};

export default HexTransmitScaleTab;
